import { lazy, Suspense, useMemo, type ReactNode } from 'react';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { brainTokens } from '../../design/brainTheme';
import { Icon } from '../../mui';
import { documentLink, parseDocumentGraph, parseDocumentResources, type VisualLanguage } from '../../lib/document-visuals';
import { VisualFrame } from './VisualFrame';

const Graph = lazy(() => import('./DocumentGraph'));
const Mermaid = lazy(() => import('./MermaidVisual'));
const c = brainTokens.color;
export function VisualBlock({ language, source, documentKey, onNavigate, renderIssue }: {
  language: VisualLanguage;
  source: string;
  documentKey?: string;
  onNavigate: (href: string) => void;
  renderIssue: (key: string) => ReactNode;
}) {
  const parsed = useMemo(() => {
    try {
      return { graph: language === 'brain-graph' ? parseDocumentGraph(source, documentKey) : null,
        resources: language === 'brain-links' ? parseDocumentResources(source, documentKey) : null };
    } catch (e) { return { error: e instanceof Error ? e.message : 'Could not read this visual.' }; }
  }, [language, source, documentKey]);
  if (parsed.error) return <VisualFrame title="Document visual" source={source} error={parsed.error}/>;
  if (parsed.resources) return <VisualFrame title={parsed.resources.title} source={source}>
    {() => <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.5 }}>
      {parsed.resources!.items.map((r, i) => {
        const target = documentLink(r.href, documentKey)!;
        return <Box key={i} sx={{ p: 1.75, border: `1px solid ${c.border}`, borderRadius: `${brainTokens.radius.card}px`, background: c.white }}>
          <ButtonBase onClick={() => onNavigate(r.href)} sx={{ textAlign: 'left', display: 'flex', width: '100%', gap: 1, alignItems: 'flex-start', color: c.tealInk, fontSize: 13.5, fontWeight: 650 }}><Box sx={{ flex: 1 }}>{r.title}</Box><Icon name={target.kind === 'external' ? 'external-link' : 'arrow-right'} size={15}/></ButtonBase>
          {r.description && <Box sx={{ fontSize: 12.5, color: c.muted, mt: .75, lineHeight: 1.5 }}>{r.description}</Box>}
          {r.issueKey && <Box sx={{ mt: 1 }}>{renderIssue(r.issueKey)}</Box>}
        </Box>;
      })}
    </Box>}
  </VisualFrame>;
  return <Suspense fallback={<Box role="status" sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', color: c.muted, fontSize: 13 }}><CircularProgress size={16}/>Loading visual…</Box>}>
    {language === 'mermaid' ? <Mermaid source={source}/> : parsed.graph ? <Graph key={source} graph={parsed.graph} source={source} onNavigate={onNavigate}/> : null}
  </Suspense>;
}
